/* eslint-disable no-console */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { formatCheck2Backend } from "../../actions/Checks/util";
import { tenantApiPath } from "../../actions/tenantApi";
import { callAvosApi } from "../../utils/useAvosApiHook";

/**
 * Mutation hook for creating a new check.
 *
 * Usage:
 *   const mutation = useCreateCheckMutation();
 *   mutation.mutate({tenantId, data: {...}});
 */
export function useCreateCheckMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (args: { tenantId: string; data: any }) => {
            const { tenantId, data } = args;

            if (!tenantId) {
                throw new Error("Cannot create check without tenantId");
            }

            const formattedData = formatCheck2Backend(data);
            const url = `${tenantApiPath(tenantId, "checks/")}`;

            const response = await callAvosApi(`/${url}`, { method: "POST", data: formattedData });
            return response.data;
        },
        onSuccess: (data, variables) => {
            // Invalidate checks list to refresh
            queryClient.invalidateQueries({
                queryKey: ["layerChecks", variables.tenantId],
            });
        },
        onError: (error) => {
            console.error("Failed to create check:", error);
        },
    });
}

/**
 * Mutation hook for deleting a check.
 *
 * Usage:
 *   const mutation = useDeleteCheckMutation();
 *   mutation.mutate({check});
 */
export function useDeleteCheckMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (args: { check: any }) => {
            const { check } = args;

            if (!check?.tenant_id || !check?.check_id) {
                throw new Error("Cannot delete check without tenant_id and check_id");
            }

            const url = `${tenantApiPath(check.tenant_id, "checks/")}${check.check_id}/`;

            const response = await callAvosApi(`/${url}`, { method: "DELETE" });
            return response.data;
        },
        onSuccess: (data, variables) => {
            // Invalidate checks list
            queryClient.invalidateQueries({
                queryKey: ["layerChecks", variables.check.tenant_id],
            });
        },
        onError: (error) => {
            console.error("Failed to delete check:", error);
        },
    });
}

/**
 * Mutation hook for completing a check.
 *
 * Usage:
 *   const mutation = useCompleteCheckMutation();
 *   mutation.mutate({check});
 */
export function useCompleteCheckMutation() {
    return useMutation({
        mutationFn: async (args: { check: any }) => {
            const { check } = args;

            if (!check?.tenant_id || !check?.check_id) {
                throw new Error(
                    "Cannot complete check without tenant_id and check_id"
                );
            }

            const url = `${tenantApiPath(check.tenant_id, "checks/")}${check.check_id}/complete`;

            const response = await callAvosApi(`/${url}`, { method: "POST", data: {} });
            return response.data;
        },
        onError: (error) => {
            console.error("Failed to complete check:", error);
        },
    });
}

/**
 * Mutation hook for triggering business rules on a check.
 *
 * Usage:
 *   const mutation = useTriggerCheckBusinessRulesMutation();
 *   mutation.mutate({check});
 */
export function useTriggerCheckBusinessRulesMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (args: { check: any }) => {
            const { check } = args;

            if (!check?.tenant_id || !check?.check_id) {
                throw new Error(
                    "Cannot trigger business rules without tenant_id and check_id"
                );
            }

            const url = `${tenantApiPath(check.tenant_id, "checks/")}${check.check_id}/business-rules`;

            const response = await callAvosApi(`/${url}`, { method: "GET" });
            return response.data;
        },
        onSuccess: (data, variables) => {
            // Business rules recompute check fields server-side; without this the
            // summary keeps rendering the pre-run values.
            queryClient.invalidateQueries({
                queryKey: ["check", variables.check.tenant_id, variables.check.check_id],
            });
            queryClient.invalidateQueries({
                queryKey: ["layerChecks", variables.check.tenant_id],
            });
        },
        onError: (error) => {
            console.error("Failed to trigger business rules:", error);
        },
    });
}

/**
 * Mutation hook for sending an email notification for a check.
 *
 * Usage:
 *   const mutation = useSendEmailNotificationMutation();
 *   mutation.mutate({check});
 */
export function useSendEmailNotificationMutation() {
    return useMutation({
        mutationFn: async (args: { check: any }) => {
            const { check } = args;

            if (!check?.tenant_id || !check?.check_id) {
                throw new Error(
                    "Cannot send email notification without tenant_id and check_id"
                );
            }

            const url = `${tenantApiPath(check.tenant_id, "checks/")}${check.check_id}/send-notification`;

            const response = await callAvosApi(`/${url}`, { method: "POST" });
            return response.data;
        },
        onError: (error) => {
            console.error("Failed to send email notification:", error);
        },
    });
}

/**
 * Mutation hook for marking fruits as calibration avocados on a check.
 *
 * Usage:
 *   const mutation = useSetAsCalibrationAvocadosMutation();
 *   mutation.mutate({check, calibrationStage, fruitIds});
 */
export function useSetAsCalibrationAvocadosMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (args: { check: any; calibrationStage: string; fruitIds: string[] }) => {
            const { check, calibrationStage, fruitIds } = args;

            if (!check?.tenant_id || !check?.check_id) {
                throw new Error(
                    "Cannot set calibration avocados without tenant_id and check_id"
                );
            }

            const url = `${tenantApiPath(check.tenant_id, "checks/")}${check.check_id}/set_as_calibration/`;

            const response = await callAvosApi(`/${url}`, {
                method: "POST",
                data: {
                    calibration_stage: calibrationStage,
                    fruit_ids: fruitIds,
                },
            });
            return response.data;
        },
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({
                queryKey: ["layerChecks", variables.check.tenant_id],
            });
        },
        onError: (error) => {
            console.error("Failed to set calibration avocados:", error);
        },
    });
}
